import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  DraggableDialog,
  DraggableDialogContent,
  DraggableDialogHeader,
  DraggableDialogBody,
  DraggableDialogTitle,
  DraggableDialogDescription,
  DraggableDialogFooter,
} from '../components/ui/draggable-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Building2,
  Plus,
  Loader2,
  Trash2,
  Image,
  Users,
  CheckCircle2,
  LogOut,
  UserPlus,
  Mail,
  Key,
  Eye,
  EyeOff,
  Settings,
  Send,
  Server,
  AlertCircle,
  Pencil,
  Bell,
  X,
  AlertTriangle,
  Info,
  CheckCircle,
  Shield,
  UserCog
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Super-Admin Logo URL
const SUPERADMIN_LOGO = "https://static.prod-images.emergentagent.com/jobs/58ba4e19-b0c8-4919-b3ef-47db920c1c4b/images/ff71c9958cec150500e10c34cb63d25394e3ad0f58561e160f385b3095582827.png";

// Super-Admin API
const superAdminApi = {
  login: (data) => api.post('/super-admin/login', data),
  getTenants: () => api.get('/tenants'),
  createTenant: (data) => api.post('/tenants', data),
  updateTenant: (id, data) => api.put(`/tenants/${id}`, data),
  deleteTenant: (id) => api.delete(`/tenants/${id}`),
  uploadLogo: async (id, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post(`/tenants/${id}/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // Benutzer-API
  getTenantUsers: (tenantId) => api.get(`/tenants/${tenantId}/users`),
  createTenantUser: (tenantId, data) => api.post(`/tenants/${tenantId}/users`, data),
  deleteTenantUser: (tenantId, userId) => api.delete(`/tenants/${tenantId}/users/${userId}`),
  // E-Mail-Einstellungen
  getEmailSettings: () => api.get('/settings/email'),
  updateEmailSettings: (data) => api.put('/settings/email', data),
  testEmail: () => api.post('/settings/email/test'),
  // Benachrichtigungen
  getNotifications: () => api.get('/notifications'),
  markNotificationRead: (id) => api.put(`/notifications/${id}/read`),
  markAllNotificationsRead: () => api.put('/notifications/read-all'),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
  // Super-Admin Verwaltung
  getSuperAdmins: () => api.get('/super-admins'),
  createSuperAdmin: (data) => api.post('/super-admins', data),
  updateSuperAdmin: (id, data) => api.put(`/super-admins/${id}`, data),
  deleteSuperAdmin: (id) => api.delete(`/super-admins/${id}`),
};

export default function SuperAdmin() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showEmailSettings, setShowEmailSettings] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('tenants');
  
  // Login Form
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loggingIn, setLoggingIn] = useState(false);
  
  // Create Tenant Form
  const [createForm, setCreateForm] = useState({
    name: '',
    slug: '',
    contact_name: '',
    contact_email: '',
    admin_email: '',
    admin_password: '',
    admin_name: ''
  });

  // Edit Tenant Form
  const [editForm, setEditForm] = useState({
    name: '',
    contact_name: '',
    contact_email: '',
    status: 'active',
    user_limit: 0
  });

  // Create User Form
  const [userForm, setUserForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'inspector'
  });

  // E-Mail Settings
  const [emailSettings, setEmailSettings] = useState({
    smtp_host: 'asmtp.mail.hostpoint.ch',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_name: 'FireCheck Wallis',
    smtp_from_email: '',
    smtp_enabled: false,
    smtp_use_tls: true,
    smtp_use_ssl: false,
  });
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);

  // Super-Admin Verwaltung
  const [superAdmins, setSuperAdmins] = useState([]);
  const [showSuperAdminDialog, setShowSuperAdminDialog] = useState(false);
  const [showEditSuperAdminDialog, setShowEditSuperAdminDialog] = useState(false);
  const [selectedSuperAdmin, setSelectedSuperAdmin] = useState(null);
  const [loadingSuperAdmins, setLoadingSuperAdmins] = useState(false);
  const [creatingSuperAdmin, setCreatingSuperAdmin] = useState(false);
  const [updatingSuperAdmin, setUpdatingSuperAdmin] = useState(false);
  const [superAdminForm, setSuperAdminForm] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [editSuperAdminForm, setEditSuperAdminForm] = useState({
    name: '',
    password: ''
  });

  // Benachrichtigungen
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsLoggedIn(true);
      fetchTenants();
      fetchEmailSettings();
      fetchNotifications();
      fetchSuperAdmins();
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const response = await superAdminApi.login(loginForm);
      localStorage.setItem('superAdminToken', response.data.access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      setIsLoggedIn(true);
      toast.success('Login erfolgreich');
      fetchTenants();
      fetchEmailSettings();
      fetchNotifications();
      fetchSuperAdmins();
    } catch (error) {
      toast.error('Login fehlgeschlagen', {
        description: error.response?.data?.detail || 'Ungültige Anmeldedaten'
      });
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('superAdminToken');
    delete api.defaults.headers.common['Authorization'];
    setIsLoggedIn(false);
    setTenants([]);
  };

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const response = await superAdminApi.getTenants();
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const response = await superAdminApi.getEmailSettings();
      setEmailSettings(response.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await superAdminApi.getNotifications();
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleMarkNotificationRead = async (id) => {
    try {
      await superAdminApi.markNotificationRead(id);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking notification read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await superAdminApi.markAllNotificationsRead();
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all read:', error);
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await superAdminApi.deleteNotification(id);
      const wasUnread = notifications.find(n => n.id === id)?.read === false;
      setNotifications(notifications.filter(n => n.id !== id));
      if (wasUnread) setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const fetchTenantUsers = async (tenant) => {
    setSelectedTenant(tenant);
    setShowUserDialog(true);
    setLoadingUsers(true);
    try {
      const response = await superAdminApi.getTenantUsers(tenant.id);
      setTenantUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setTenantUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await superAdminApi.createTenant(createForm);
      toast.success('Firma erstellt', {
        description: `${createForm.name} wurde erfolgreich erstellt`
      });
      setShowCreateDialog(false);
      setCreateForm({
        name: '',
        slug: '',
        contact_name: '',
        contact_email: '',
        admin_email: '',
        admin_password: '',
        admin_name: ''
      });
      fetchTenants();
    } catch (error) {
      toast.error('Fehler beim Erstellen', {
        description: error.response?.data?.detail || 'Bitte versuchen Sie es erneut'
      });
    } finally {
      setCreating(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreatingUser(true);
    try {
      await superAdminApi.createTenantUser(selectedTenant.id, userForm);
      toast.success('Benutzer erstellt', {
        description: `${userForm.name} wurde erfolgreich erstellt`
      });
      setUserForm({ email: '', password: '', name: '', role: 'inspector' });
      fetchTenantUsers(selectedTenant);
      fetchTenants(); // Update user count
      fetchNotifications(); // Check for new notifications (80% limit)
    } catch (error) {
      toast.error('Fehler beim Erstellen', {
        description: error.response?.data?.detail || 'Bitte versuchen Sie es erneut'
      });
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Möchten Sie diesen Benutzer wirklich löschen?')) return;
    try {
      await superAdminApi.deleteTenantUser(selectedTenant.id, userId);
      toast.success('Benutzer gelöscht');
      fetchTenantUsers(selectedTenant);
      fetchTenants(); // Update user count
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleDeleteTenant = async (tenant) => {
    if (!confirm(`Möchten Sie "${tenant.name}" wirklich löschen?`)) return;
    try {
      await superAdminApi.deleteTenant(tenant.id);
      toast.success('Firma gelöscht');
      fetchTenants();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const openEditDialog = (tenant) => {
    setSelectedTenant(tenant);
    setEditForm({
      name: tenant.name,
      contact_name: tenant.contact_name,
      contact_email: tenant.contact_email,
      status: tenant.status || 'active',
      user_limit: tenant.user_limit || 0
    });
    setShowEditDialog(true);
  };

  const handleUpdateTenant = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      await superAdminApi.updateTenant(selectedTenant.id, editForm);
      toast.success('Firma aktualisiert', {
        description: `${editForm.name} wurde erfolgreich aktualisiert`
      });
      setShowEditDialog(false);
      setSelectedTenant(null);
      fetchTenants();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren', {
        description: error.response?.data?.detail || 'Bitte versuchen Sie es erneut'
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleLogoUpload = async (e, tenantId) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      await superAdminApi.uploadLogo(tenantId, file);
      toast.success('Logo hochgeladen');
      fetchTenants();
    } catch (error) {
      toast.error('Fehler beim Hochladen');
    }
  };

  const handleSaveEmailSettings = async (e) => {
    e.preventDefault();
    setSavingEmail(true);
    try {
      // Stelle sicher, dass smtp_use_tls/smtp_use_ssl gesendet werden
      const payload = {
        smtp_host: emailSettings.smtp_host,
        smtp_port: emailSettings.smtp_port,
        smtp_user: emailSettings.smtp_user,
        smtp_password: emailSettings.smtp_password || '********',
        smtp_from_name: emailSettings.smtp_from_name,
        smtp_from_email: emailSettings.smtp_from_email,
        smtp_enabled: emailSettings.smtp_enabled,
        smtp_use_tls: emailSettings.smtp_use_tls ?? true,
        smtp_use_ssl: emailSettings.smtp_use_ssl ?? false,
      };
      await superAdminApi.updateEmailSettings(payload);
      toast.success('E-Mail-Einstellungen gespeichert');
      fetchEmailSettings();
    } catch (error) {
      toast.error('Fehler beim Speichern', {
        description: error.response?.data?.detail || 'Bitte versuchen Sie es erneut'
      });
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const response = await superAdminApi.testEmail();
      toast.success('Test erfolgreich', {
        description: response.data.message
      });
    } catch (error) {
      toast.error('Test fehlgeschlagen', {
        description: error.response?.data?.detail || 'E-Mail konnte nicht gesendet werden'
      });
    } finally {
      setTestingEmail(false);
    }
  };

  // Super-Admin Verwaltung
  const fetchSuperAdmins = async () => {
    setLoadingSuperAdmins(true);
    try {
      const response = await superAdminApi.getSuperAdmins();
      console.log('Super-Admins API response:', response.data);
      setSuperAdmins(response.data || []);
    } catch (error) {
      console.error('Error fetching super admins:', error);
      setSuperAdmins([]);
    } finally {
      setLoadingSuperAdmins(false);
    }
  };

  const handleCreateSuperAdmin = async (e) => {
    e.preventDefault();
    setCreatingSuperAdmin(true);
    try {
      await superAdminApi.createSuperAdmin(superAdminForm);
      toast.success('Super-Admin erstellt', {
        description: `${superAdminForm.name} wurde erfolgreich erstellt`
      });
      setShowSuperAdminDialog(false);
      setSuperAdminForm({ email: '', password: '', name: '' });
      fetchSuperAdmins();
    } catch (error) {
      toast.error('Fehler beim Erstellen', {
        description: error.response?.data?.detail || 'Bitte versuchen Sie es erneut'
      });
    } finally {
      setCreatingSuperAdmin(false);
    }
  };

  const openEditSuperAdminDialog = (admin) => {
    setSelectedSuperAdmin(admin);
    setEditSuperAdminForm({
      name: admin.name,
      password: ''
    });
    setShowEditSuperAdminDialog(true);
  };

  const handleUpdateSuperAdmin = async (e) => {
    e.preventDefault();
    setUpdatingSuperAdmin(true);
    try {
      const updateData = { name: editSuperAdminForm.name };
      if (editSuperAdminForm.password) {
        updateData.password = editSuperAdminForm.password;
      }
      await superAdminApi.updateSuperAdmin(selectedSuperAdmin.id, updateData);
      toast.success('Super-Admin aktualisiert');
      setShowEditSuperAdminDialog(false);
      setSelectedSuperAdmin(null);
      fetchSuperAdmins();
    } catch (error) {
      toast.error('Fehler beim Aktualisieren', {
        description: error.response?.data?.detail || 'Bitte versuchen Sie es erneut'
      });
    } finally {
      setUpdatingSuperAdmin(false);
    }
  };

  const handleDeleteSuperAdmin = async (admin) => {
    if (!confirm(`Möchten Sie "${admin.name}" (${admin.email}) wirklich löschen?`)) return;
    try {
      await superAdminApi.deleteSuperAdmin(admin.id);
      toast.success('Super-Admin gelöscht');
      fetchSuperAdmins();
    } catch (error) {
      toast.error('Fehler beim Löschen', {
        description: error.response?.data?.detail || 'Bitte versuchen Sie es erneut'
      });
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[äÄ]/g, 'ae')
      .replace(/[öÖ]/g, 'oe')
      .replace(/[üÜ]/g, 'ue')
      .replace(/[ß]/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Login Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-20 h-20 rounded-xl overflow-hidden mb-4 bg-gradient-to-br from-red-500 to-red-600 p-2">
              <img src={SUPERADMIN_LOGO} alt="FireCheck" className="w-full h-full object-contain" />
            </div>
            <CardTitle className="text-2xl">FireCheck Wallis</CardTitle>
            <CardDescription>
              Super-Admin Portal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="superadmin@firecheck.ch"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-red-500 hover:bg-red-600" disabled={loggingIn}>
                {loggingIn && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Anmelden
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Button variant="link" onClick={() => navigate('/login')}>
                Zurück zur normalen Anmeldung
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl overflow-hidden bg-gradient-to-br from-red-500 to-red-600 p-1.5">
              <img src={SUPERADMIN_LOGO} alt="FireCheck" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-bold text-lg">FireCheck Wallis</h1>
              <p className="text-xs text-muted-foreground">Super-Admin Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Notifications Bell */}
            <div className="relative">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative"
              >
                <Bell className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
              
              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-96 bg-card border rounded-lg shadow-lg z-50">
                  <div className="flex items-center justify-between p-3 border-b">
                    <h3 className="font-semibold">Benachrichtigungen</h3>
                    <div className="flex gap-2">
                      {unreadCount > 0 && (
                        <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
                          Alle gelesen
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => setShowNotifications(false)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center text-muted-foreground">
                        <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>Keine Benachrichtigungen</p>
                      </div>
                    ) : (
                      notifications.map(notification => (
                        <div 
                          key={notification.id} 
                          className={`p-3 border-b hover:bg-muted/50 cursor-pointer ${!notification.read ? 'bg-muted/30' : ''}`}
                          onClick={() => handleMarkNotificationRead(notification.id)}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`p-1.5 rounded-full ${
                              notification.type === 'warning' ? 'bg-amber-500/20 text-amber-500' :
                              notification.type === 'error' ? 'bg-red-500/20 text-red-500' :
                              notification.type === 'success' ? 'bg-green-500/20 text-green-500' :
                              'bg-blue-500/20 text-blue-500'
                            }`}>
                              {notification.type === 'warning' ? <AlertTriangle className="h-4 w-4" /> :
                               notification.type === 'error' ? <AlertCircle className="h-4 w-4" /> :
                               notification.type === 'success' ? <CheckCircle className="h-4 w-4" /> :
                               <Info className="h-4 w-4" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium ${!notification.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                                {notification.title}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">
                                {notification.message}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                {new Date(notification.created_at).toLocaleString('de-CH')}
                              </p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              className="h-6 w-6 opacity-50 hover:opacity-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteNotification(notification.id);
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <Button variant="ghost" onClick={handleLogout} className="gap-2">
              <LogOut className="h-4 w-4" />
              Abmelden
            </Button>
          </div>
        </div>
      </header>

      {/* Main Tabs */}
      <main className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-lg grid-cols-3 mb-8">
            <TabsTrigger value="tenants" className="gap-2">
              <Building2 className="h-4 w-4" />
              Firmen
            </TabsTrigger>
            <TabsTrigger value="admins" className="gap-2">
              <Shield className="h-4 w-4" />
              Super-Admins
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2">
              <Settings className="h-4 w-4" />
              Einstellungen
            </TabsTrigger>
          </TabsList>

          {/* Firmen Tab */}
          <TabsContent value="tenants">
            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-red-500/10 rounded-lg flex items-center justify-center">
                      <Building2 className="h-6 w-6 text-red-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{tenants.filter(t => t.status === 'active').length}</p>
                      <p className="text-sm text-muted-foreground">Aktive Firmen</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                      <CheckCircle2 className="h-6 w-6 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{tenants.length}</p>
                      <p className="text-sm text-muted-foreground">Firmen gesamt</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6 flex items-center justify-center">
                  <Button onClick={() => setShowCreateDialog(true)} className="gap-2 bg-red-500 hover:bg-red-600">
                    <Plus className="h-4 w-4" />
                    Neue Firma erstellen
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Tenants Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Firmen
                </CardTitle>
                <CardDescription>
                  Verwalten Sie alle registrierten Firmen und deren Benutzer
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : tenants.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Noch keine Firmen vorhanden</p>
                    <Button onClick={() => setShowCreateDialog(true)} className="mt-4 gap-2 bg-red-500 hover:bg-red-600">
                      <Plus className="h-4 w-4" />
                      Erste Firma erstellen
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Logo</TableHead>
                        <TableHead>Firma</TableHead>
                        <TableHead>Slug</TableHead>
                        <TableHead>Kontakt</TableHead>
                        <TableHead>Benutzer</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Erstellt</TableHead>
                        <TableHead className="text-right">Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tenants.map((tenant) => (
                        <TableRow key={tenant.id}>
                          <TableCell>
                            <div className="relative group">
                              {tenant.logo_url ? (
                                <img
                                  src={tenant.logo_url}
                                  alt={tenant.name}
                                  className="h-10 w-10 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="h-10 w-10 bg-muted rounded-lg flex items-center justify-center">
                                  <Building2 className="h-5 w-5 text-muted-foreground" />
                                </div>
                              )}
                              <label className="absolute inset-0 cursor-pointer opacity-0 group-hover:opacity-100 bg-black/50 rounded-lg flex items-center justify-center transition-opacity">
                                <Image className="h-4 w-4 text-white" />
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handleLogoUpload(e, tenant.id)}
                                />
                              </label>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{tenant.name}</TableCell>
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-1 rounded">{tenant.slug}</code>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <p>{tenant.contact_name}</p>
                              <p className="text-muted-foreground">{tenant.contact_email}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">
                              <span className="font-medium">{tenant.user_count || 0}</span>
                              {tenant.user_limit > 0 && (
                                <span className="text-muted-foreground"> / {tenant.user_limit}</span>
                              )}
                              {tenant.user_limit === 0 && (
                                <span className="text-muted-foreground text-xs ml-1">(∞)</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'} className={tenant.status === 'active' ? 'bg-green-500' : ''}>
                              {tenant.status === 'active' ? 'Aktiv' : 'Inaktiv'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(tenant.created_at).toLocaleDateString('de-CH')}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditDialog(tenant)}
                                className="gap-1"
                              >
                                <Pencil className="h-4 w-4" />
                                Bearbeiten
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => fetchTenantUsers(tenant)}
                                className="gap-1"
                              >
                                <Users className="h-4 w-4" />
                                Benutzer
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeleteTenant(tenant)}
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Super-Admins Tab */}
          <TabsContent value="admins">
            <div className="space-y-6">
              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                        <Shield className="h-6 w-6 text-purple-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{superAdmins.length}</p>
                        <p className="text-sm text-muted-foreground">Super-Admins</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6 flex items-center justify-center">
                    <Button onClick={() => setShowSuperAdminDialog(true)} className="gap-2 bg-purple-500 hover:bg-purple-600">
                      <Plus className="h-4 w-4" />
                      Neuen Super-Admin erstellen
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Super-Admins Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    Super-Administratoren
                  </CardTitle>
                  <CardDescription>
                    Verwalten Sie die Super-Administratoren mit Vollzugriff auf alle Firmen
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingSuperAdmins ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : superAdmins.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Noch keine Super-Admins vorhanden</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>E-Mail</TableHead>
                          <TableHead>Erstellt</TableHead>
                          <TableHead className="text-right">Aktionen</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {superAdmins.map((admin) => (
                          <TableRow key={admin.id}>
                            <TableCell className="font-medium">{admin.name}</TableCell>
                            <TableCell>{admin.email}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {new Date(admin.created_at).toLocaleDateString('de-CH')}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openEditSuperAdminDialog(admin)}
                                  className="gap-1"
                                >
                                  <Pencil className="h-4 w-4" />
                                  Bearbeiten
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteSuperAdmin(admin)}
                                  className="text-destructive hover:text-destructive"
                                  disabled={superAdmins.length <= 1}
                                  title={superAdmins.length <= 1 ? "Der letzte Super-Admin kann nicht gelöscht werden" : "Löschen"}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Hinweis */}
              <Card className="border-purple-500/20 bg-purple-500/5">
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <Shield className="h-5 w-5 text-purple-500 shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="font-medium text-purple-500">Über Super-Admins</p>
                      <p className="text-sm text-muted-foreground">
                        Super-Admins haben Vollzugriff auf alle Firmen und deren Daten. 
                        Sie können Firmen erstellen, bearbeiten und löschen sowie alle Benutzer verwalten.
                      </p>
                      <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                        <li>Es muss immer mindestens ein Super-Admin existieren</li>
                        <li>Super-Admins können sich nicht selbst löschen</li>
                        <li>Passwörter werden sicher verschlüsselt gespeichert</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Einstellungen Tab */}
          <TabsContent value="settings">
            <div className="grid gap-8">
              {/* E-Mail-Einstellungen */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    E-Mail-Einstellungen
                  </CardTitle>
                  <CardDescription>
                    Konfigurieren Sie den SMTP-Server für den E-Mail-Versand
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSaveEmailSettings} className="space-y-6">
                    {/* Status */}
                    <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/50">
                      <div className="flex items-center gap-3">
                        {emailSettings.smtp_enabled ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        ) : (
                          <AlertCircle className="h-5 w-5 text-amber-500" />
                        )}
                        <div>
                          <p className="font-medium">
                            {emailSettings.smtp_enabled ? 'E-Mail aktiv' : 'E-Mail nicht konfiguriert'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {emailSettings.smtp_enabled 
                              ? `Absender: ${emailSettings.smtp_from_email}` 
                              : 'Bitte konfigurieren Sie die SMTP-Einstellungen'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor="smtp_enabled" className="text-sm">Aktiviert</Label>
                        <Switch
                          id="smtp_enabled"
                          checked={emailSettings.smtp_enabled}
                          onCheckedChange={(checked) => setEmailSettings({ ...emailSettings, smtp_enabled: checked })}
                        />
                      </div>
                    </div>

                    {/* SMTP Server */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="smtp_host">SMTP-Server *</Label>
                        <div className="relative">
                          <Server className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="smtp_host"
                            value={emailSettings.smtp_host}
                            onChange={(e) => setEmailSettings({ ...emailSettings, smtp_host: e.target.value })}
                            placeholder="asmtp.mail.hostpoint.ch"
                            className="pl-10"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="smtp_port">Port *</Label>
                        <Input
                          id="smtp_port"
                          type="number"
                          value={emailSettings.smtp_port}
                          onChange={(e) => setEmailSettings({ ...emailSettings, smtp_port: parseInt(e.target.value) || 587 })}
                          placeholder="587"
                          required
                        />
                      </div>
                    </div>

                    {/* Anmeldedaten */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="smtp_user">Benutzername/E-Mail *</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="smtp_user"
                            value={emailSettings.smtp_user}
                            onChange={(e) => setEmailSettings({ ...emailSettings, smtp_user: e.target.value })}
                            placeholder="ihre-email@domain.ch"
                            className="pl-10"
                            required
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="smtp_password">Passwort *</Label>
                        <div className="relative">
                          <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            id="smtp_password"
                            type={showSmtpPassword ? 'text' : 'password'}
                            value={emailSettings.smtp_password}
                            onChange={(e) => setEmailSettings({ ...emailSettings, smtp_password: e.target.value })}
                            placeholder={emailSettings.smtp_password_set ? '••••••••' : 'SMTP Passwort'}
                            className="pl-10 pr-10"
                            required={!emailSettings.smtp_password_set}
                          />
                          <button
                            type="button"
                            onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showSmtpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Absender */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label htmlFor="smtp_from_name">Absender-Name *</Label>
                        <Input
                          id="smtp_from_name"
                          value={emailSettings.smtp_from_name}
                          onChange={(e) => setEmailSettings({ ...emailSettings, smtp_from_name: e.target.value })}
                          placeholder="FireCheck Wallis"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="smtp_from_email">Absender-E-Mail *</Label>
                        <Input
                          id="smtp_from_email"
                          type="email"
                          value={emailSettings.smtp_from_email}
                          onChange={(e) => setEmailSettings({ ...emailSettings, smtp_from_email: e.target.value })}
                          placeholder="noreply@domain.ch"
                          required
                          data-testid="smtp-from-email-input"
                        />
                        {(() => {
                          const fromEmail = (emailSettings.smtp_from_email || '').trim().toLowerCase();
                          const user = (emailSettings.smtp_user || '').trim().toLowerCase();
                          if (!fromEmail || !user || !fromEmail.includes('@') || !user.includes('@')) return null;
                          const fromDomain = fromEmail.split('@')[1];
                          const userDomain = user.split('@')[1];
                          if (fromDomain && userDomain && fromDomain !== userDomain) {
                            return (
                              <div className="flex gap-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-xs" data-testid="smtp-domain-warning">
                                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                                <div>
                                  <p className="font-medium">Achtung: unterschiedliche Domains</p>
                                  <p className="mt-1">
                                    Absender <b>@{fromDomain}</b> passt nicht zum SMTP-Login <b>@{userDomain}</b>. Dies führt fast immer zu Spam-Ablehnungen (SPF/DKIM/DMARC).
                                    Wählen Sie eine Absenderadresse auf <b>@{userDomain}</b> oder eine dort autorisierte Alias-Adresse.
                                  </p>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4 pt-4">
                      <Button type="submit" disabled={savingEmail} className="bg-red-500 hover:bg-red-600">
                        {savingEmail && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Einstellungen speichern
                      </Button>
                      {emailSettings.smtp_enabled && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          onClick={handleTestEmail}
                          disabled={testingEmail}
                        >
                          {testingEmail ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          ) : (
                            <Send className="h-4 w-4 mr-2" />
                          )}
                          Test-E-Mail senden
                        </Button>
                      )}
                    </div>
                  </form>
                </CardContent>
              </Card>

              {/* Hinweis */}
              <Card className="border-amber-500/20 bg-amber-500/5">
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="font-medium text-amber-500">Hostpoint CloudOffice Einstellungen</p>
                      <p className="text-sm text-muted-foreground">
                        Für Hostpoint CloudOffice verwenden Sie folgende Einstellungen:
                      </p>
                      <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
                        <li>SMTP-Server: <code className="bg-muted px-1 rounded">asmtp.mail.hostpoint.ch</code></li>
                        <li>Port: <code className="bg-muted px-1 rounded">587</code> (STARTTLS)</li>
                        <li>Benutzername: Ihre vollständige E-Mail-Adresse</li>
                        <li>Passwort: Ihr E-Mail-Passwort</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Tenant Dialog (Verschiebbar) */}
      <DraggableDialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DraggableDialogContent className="max-w-lg">
          <DraggableDialogHeader>
            <DraggableDialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5" />
              Neue Firma erstellen
            </DraggableDialogTitle>
            <DraggableDialogDescription>
              Erstellen Sie eine neue Firma mit eigenem Administrator
            </DraggableDialogDescription>
          </DraggableDialogHeader>
          
          <DraggableDialogBody>
            <form id="create-tenant-form" onSubmit={handleCreateTenant} className="space-y-4">
              <div className="space-y-4 border-b border-border pb-4">
                <h3 className="font-medium text-sm text-muted-foreground">Firmeninformationen</h3>
                <div className="space-y-2">
                  <Label htmlFor="name">Firmenname *</Label>
                  <Input
                    id="name"
                    value={createForm.name}
                    onChange={(e) => {
                      setCreateForm({
                        ...createForm,
                        name: e.target.value,
                        slug: generateSlug(e.target.value)
                      });
                    }}
                    placeholder="Brandschutz Wallis AG"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">URL-Slug *</Label>
                  <Input
                    id="slug"
                    value={createForm.slug}
                    onChange={(e) => setCreateForm({ ...createForm, slug: e.target.value })}
                    placeholder="brandschutz-wallis"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact_name">Ansprechpartner *</Label>
                    <Input
                      id="contact_name"
                      value={createForm.contact_name}
                      onChange={(e) => setCreateForm({ ...createForm, contact_name: e.target.value })}
                      placeholder="Max Muster"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact_email">Kontakt-E-Mail *</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      value={createForm.contact_email}
                      onChange={(e) => setCreateForm({ ...createForm, contact_email: e.target.value })}
                      placeholder="info@firma.ch"
                      required
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-medium text-sm text-muted-foreground">Administrator-Konto</h3>
                <div className="space-y-2">
                  <Label htmlFor="admin_name">Name *</Label>
                  <Input
                    id="admin_name"
                    value={createForm.admin_name}
                    onChange={(e) => setCreateForm({ ...createForm, admin_name: e.target.value })}
                    placeholder="Max Muster"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin_email">E-Mail *</Label>
                  <Input
                    id="admin_email"
                    type="email"
                    value={createForm.admin_email}
                    onChange={(e) => setCreateForm({ ...createForm, admin_email: e.target.value })}
                    placeholder="admin@firma.ch"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="admin_password">Passwort *</Label>
                  <Input
                    id="admin_password"
                    type="password"
                    value={createForm.admin_password}
                    onChange={(e) => setCreateForm({ ...createForm, admin_password: e.target.value })}
                    placeholder="Sicheres Passwort"
                    required
                    minLength={6}
                  />
                </div>
              </div>
            </form>
          </DraggableDialogBody>
          
          <DraggableDialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="create-tenant-form" disabled={creating} className="bg-red-500 hover:bg-red-600">
              {creating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Firma erstellen
            </Button>
          </DraggableDialogFooter>
        </DraggableDialogContent>
      </DraggableDialog>

      {/* User Management Dialog (Verschiebbar) */}
      <DraggableDialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DraggableDialogContent className="max-w-2xl">
          <DraggableDialogHeader>
            <DraggableDialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Benutzer von {selectedTenant?.name}
            </DraggableDialogTitle>
            <DraggableDialogDescription>
              Verwalten Sie die Benutzer dieser Firma
            </DraggableDialogDescription>
          </DraggableDialogHeader>
          
          <DraggableDialogBody>
            <Tabs defaultValue="list" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="list">Benutzer-Liste</TabsTrigger>
                <TabsTrigger value="create">Neuer Benutzer</TabsTrigger>
              </TabsList>
              
              <TabsContent value="list" className="mt-4">
                {loadingUsers ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  </div>
                ) : tenantUsers.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Noch keine Benutzer vorhanden</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>E-Mail</TableHead>
                        <TableHead>Rolle</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Aktionen</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {tenantUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                              {user.role === 'admin' ? 'Admin' : 'Prüfer'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={user.status === 'approved' ? 'default' : 'secondary'} className={user.status === 'approved' ? 'bg-green-500' : ''}>
                              {user.status === 'approved' ? 'Aktiv' : user.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
              
              <TabsContent value="create" className="mt-4">
                <form onSubmit={handleCreateUser} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="user_name">Name *</Label>
                    <Input
                      id="user_name"
                      value={userForm.name}
                      onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                      placeholder="Hans Muster"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user_email">E-Mail *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="user_email"
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                        placeholder="hans@firma.ch"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user_password">Passwort *</Label>
                    <div className="relative">
                      <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="user_password"
                        type={showPassword ? 'text' : 'password'}
                        value={userForm.password}
                        onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                        placeholder="Sicheres Passwort"
                        className="pl-10 pr-10"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="user_role">Rolle *</Label>
                    <Select value={userForm.role} onValueChange={(value) => setUserForm({ ...userForm, role: value })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Rolle auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inspector">Prüfer</SelectItem>
                        <SelectItem value="admin">Administrator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" disabled={creatingUser} className="w-full bg-red-500 hover:bg-red-600">
                    {creatingUser && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    <UserPlus className="h-4 w-4 mr-2" />
                    Benutzer erstellen
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </DraggableDialogBody>
        </DraggableDialogContent>
      </DraggableDialog>

      {/* Edit Tenant Dialog (Verschiebbar) */}
      <DraggableDialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DraggableDialogContent className="max-w-lg">
          <DraggableDialogHeader>
            <DraggableDialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Firma bearbeiten
            </DraggableDialogTitle>
            <DraggableDialogDescription>
              Bearbeiten Sie die Daten von "{selectedTenant?.name}"
            </DraggableDialogDescription>
          </DraggableDialogHeader>
          
          <DraggableDialogBody>
            <form id="edit-tenant-form" onSubmit={handleUpdateTenant} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_name">Firmenname *</Label>
                <Input
                  id="edit_name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  placeholder="Brandschutz Wallis AG"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit_contact_name">Ansprechpartner *</Label>
                  <Input
                    id="edit_contact_name"
                    value={editForm.contact_name}
                    onChange={(e) => setEditForm({ ...editForm, contact_name: e.target.value })}
                    placeholder="Max Muster"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit_contact_email">Kontakt-E-Mail *</Label>
                  <Input
                    id="edit_contact_email"
                    type="email"
                    value={editForm.contact_email}
                    onChange={(e) => setEditForm({ ...editForm, contact_email: e.target.value })}
                    placeholder="info@firma.ch"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_status">Status</Label>
                <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Status auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Aktiv</SelectItem>
                    <SelectItem value="inactive">Inaktiv</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_user_limit">Benutzer-Limit</Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="edit_user_limit"
                    type="number"
                    min="0"
                    value={editForm.user_limit}
                    onChange={(e) => setEditForm({ ...editForm, user_limit: parseInt(e.target.value) || 0 })}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">
                    {editForm.user_limit === 0 ? '(Unbegrenzt)' : `Max. ${editForm.user_limit} Benutzer`}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">
                  0 = Unbegrenzte Benutzer. Aktuell: {selectedTenant?.user_count || 0} Benutzer
                </p>
              </div>

              <div className="p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
                <p><strong>URL-Slug:</strong> <code className="bg-muted px-1 rounded">{selectedTenant?.slug}</code></p>
                <p className="text-xs mt-1">Der URL-Slug kann nicht geändert werden.</p>
              </div>
            </form>
          </DraggableDialogBody>
          
          <DraggableDialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="edit-tenant-form" disabled={updating} className="bg-red-500 hover:bg-red-600">
              {updating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </DraggableDialogFooter>
        </DraggableDialogContent>
      </DraggableDialog>

      {/* Create Super-Admin Dialog */}
      <DraggableDialog open={showSuperAdminDialog} onOpenChange={setShowSuperAdminDialog}>
        <DraggableDialogContent className="max-w-md">
          <DraggableDialogHeader>
            <DraggableDialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Neuen Super-Admin erstellen
            </DraggableDialogTitle>
            <DraggableDialogDescription>
              Erstellen Sie einen neuen Super-Administrator
            </DraggableDialogDescription>
          </DraggableDialogHeader>
          
          <DraggableDialogBody>
            <form id="create-super-admin-form" onSubmit={handleCreateSuperAdmin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sa_name">Name *</Label>
                <Input
                  id="sa_name"
                  value={superAdminForm.name}
                  onChange={(e) => setSuperAdminForm({ ...superAdminForm, name: e.target.value })}
                  placeholder="Max Muster"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sa_email">E-Mail *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="sa_email"
                    type="email"
                    value={superAdminForm.email}
                    onChange={(e) => setSuperAdminForm({ ...superAdminForm, email: e.target.value })}
                    placeholder="admin@firecheck.ch"
                    className="pl-10"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sa_password">Passwort *</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="sa_password"
                    type="password"
                    value={superAdminForm.password}
                    onChange={(e) => setSuperAdminForm({ ...superAdminForm, password: e.target.value })}
                    placeholder="Sicheres Passwort (min. 6 Zeichen)"
                    className="pl-10"
                    required
                    minLength={6}
                  />
                </div>
              </div>
            </form>
          </DraggableDialogBody>
          
          <DraggableDialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowSuperAdminDialog(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="create-super-admin-form" disabled={creatingSuperAdmin} className="bg-purple-500 hover:bg-purple-600">
              {creatingSuperAdmin && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Super-Admin erstellen
            </Button>
          </DraggableDialogFooter>
        </DraggableDialogContent>
      </DraggableDialog>

      {/* Edit Super-Admin Dialog */}
      <DraggableDialog open={showEditSuperAdminDialog} onOpenChange={setShowEditSuperAdminDialog}>
        <DraggableDialogContent className="max-w-md">
          <DraggableDialogHeader>
            <DraggableDialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Super-Admin bearbeiten
            </DraggableDialogTitle>
            <DraggableDialogDescription>
              Bearbeiten Sie "{selectedSuperAdmin?.name}"
            </DraggableDialogDescription>
          </DraggableDialogHeader>
          
          <DraggableDialogBody>
            <form id="edit-super-admin-form" onSubmit={handleUpdateSuperAdmin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_sa_name">Name *</Label>
                <Input
                  id="edit_sa_name"
                  value={editSuperAdminForm.name}
                  onChange={(e) => setEditSuperAdminForm({ ...editSuperAdminForm, name: e.target.value })}
                  placeholder="Max Muster"
                  required
                />
              </div>
              <div className="p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
                <p><strong>E-Mail:</strong> {selectedSuperAdmin?.email}</p>
                <p className="text-xs mt-1">Die E-Mail-Adresse kann nicht geändert werden.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_sa_password">Neues Passwort (optional)</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="edit_sa_password"
                    type="password"
                    value={editSuperAdminForm.password}
                    onChange={(e) => setEditSuperAdminForm({ ...editSuperAdminForm, password: e.target.value })}
                    placeholder="Leer lassen um nicht zu ändern"
                    className="pl-10"
                    minLength={6}
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Lassen Sie das Feld leer, wenn Sie das Passwort nicht ändern möchten.
                </p>
              </div>
            </form>
          </DraggableDialogBody>
          
          <DraggableDialogFooter>
            <Button type="button" variant="outline" onClick={() => setShowEditSuperAdminDialog(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="edit-super-admin-form" disabled={updatingSuperAdmin} className="bg-purple-500 hover:bg-purple-600">
              {updatingSuperAdmin && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Speichern
            </Button>
          </DraggableDialogFooter>
        </DraggableDialogContent>
      </DraggableDialog>
    </div>
  );
}
